import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Edit2 } from "react-feather";
import { programService } from "@/services/program.service";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from "@/components/ui/dialog";
import type { ProgramAffiliateRow } from "./AffiliateRosterTable";

// Mirrors the backend rule: codes are typed at checkout and double as URL
// paths for the short discount URL.
const CODE_PATTERN = /^[A-Z0-9_-]{3,50}$/;

// Same shaping as the affiliate join form: no spaces, upper case.
const formatCode = (value: string) => value.replace(/\s+/g, "").toUpperCase();

function errorMessage(err: unknown): string {
  const message = (err as AxiosError<{ message?: string | string[] }>)?.response
    ?.data?.message;
  if (Array.isArray(message)) return message.join(" ");
  return message || "Could not change the code. Please try again.";
}

type ChangeAffiliateCodeModalProps = {
  programId: string;
  /** The affiliate whose code is being changed; null closes the dialog. */
  affiliate: ProgramAffiliateRow | null;
  onClose: () => void;
  onChanged: (result: { code: string; previousCode: string }) => void;
};

export function ChangeAffiliateCodeModal({
  programId,
  affiliate,
  onClose,
  onChanged,
}: ChangeAffiliateCodeModalProps) {
  const [code, setCode] = useState("");

  const mutation = useMutation({
    mutationFn: (newCode: string) =>
      programService.changeAffiliateCode(programId, affiliate!.id, newCode),
    onSuccess: (result) => onChanged(result),
  });

  // Fresh form each time the dialog opens for someone.
  useEffect(() => {
    setCode("");
    mutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [affiliate?.id]);

  const busy = mutation.isPending;
  const currentCode = affiliate?.code ?? "";
  const name =
    `${affiliate?.firstName ?? ""} ${affiliate?.lastName ?? ""}`.trim() ||
    affiliate?.email ||
    "this affiliate";

  const sameAsCurrent =
    code.length > 0 && code.toLowerCase() === currentCode.toLowerCase();
  const formatProblem =
    code.length === 0
      ? null
      : !CODE_PATTERN.test(code)
        ? 'Use 3-50 letters, numbers, "-" or "_".'
        : sameAsCurrent
          ? "That's already their code."
          : null;
  const canSubmit = code.length > 0 && !formatProblem && !busy;

  const close = () => {
    if (!busy) onClose();
  };

  return (
    <DialogRoot
      open={affiliate !== null}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <DialogContent>
        <DialogHeader showCloseButton onClose={close}>
          <span className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-100">
              <Edit2 className="h-5 w-5 text-brand-700" />
            </span>
            Change discount code
          </span>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) mutation.mutate(code);
          }}
        >
          <p className="text-sm text-muted-foreground">
            Give <span className="font-medium text-foreground">{name}</span> a
            new code. Their current code{" "}
            <code className="rounded bg-muted px-1.5 py-0.5 text-xs text-foreground">
              {currentCode}
            </code>{" "}
            stops working at checkout right away. Sales they've already made
            stay credited to them, and we'll email them the new code.
          </p>

          <div className="mt-5 space-y-2">
            <Label htmlFor="new-affiliate-code">New code</Label>
            <Input
              id="new-affiliate-code"
              value={code}
              onChange={(e) => {
                setCode(formatCode(e.target.value));
                if (mutation.isError) mutation.reset();
              }}
              placeholder="e.g. JANE20"
              maxLength={50}
              autoFocus
              autoComplete="off"
              spellCheck={false}
              disabled={busy}
              aria-invalid={formatProblem ? true : undefined}
              aria-describedby="new-affiliate-code-help"
              className="font-mono uppercase placeholder:normal-case"
            />
            <p
              id="new-affiliate-code-help"
              className={
                formatProblem
                  ? "text-xs text-destructive"
                  : "text-xs text-muted-foreground"
              }
            >
              {formatProblem ??
                'Letters, numbers, "-" or "_". Must be unused in your store.'}
            </p>
          </div>

          {mutation.isError && (
            <Alert variant="destructive" className="mt-4">
              <AlertDescription>
                {errorMessage(mutation.error)}
              </AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={close}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {busy && <Spinner size="sm" className="border-current" />}
              {busy ? "Changing…" : "Change code"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  );
}
